import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import type { DefaultOptions } from "@tanstack/react-query";
import type { PersistQueryClientOptions } from "@tanstack/react-query-persist-client";

import { apiErrorStatus } from "@/core/lib/api-errors";
import { handleChunkLoadError } from "@/core/lib/chunk-reload";
import { PERSISTED_USER_CACHE_KEY } from "@/core/lib/query-persistence-key";
import { appVersion } from "@/core/lib/runtime-env";
import { addToast } from "@/core/lib/toast";

declare module "@tanstack/react-query" {
  interface Register {
    mutationMeta: {
      /**
       * Title shown by the global error toast. Accepts a string or a thunk;
       * thunks are preferred so the i18n message is only evaluated when an
       * error actually fires (not on every hook render).
       */
      errorMessage?: string | (() => string);
      /** Optional description for the global error toast. Same string-or-thunk shape. */
      errorDescription?: string | (() => string);
      /** Suppress the global error toast for this mutation (caller handles errors itself). */
      skipErrorToast?: boolean;
    };
  }
}

function resolveMetaMessage(
  value: string | (() => string) | undefined,
): string | undefined {
  if (typeof value === "function") return value();
  return value;
}

// the single place where every query error in the app passes through: a
// stale-chunk failure inside any queryFn recovers here instead of surfacing
// as a feature-level error
export function createQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error) => {
      handleChunkLoadError(error, "query");
    },
  });
}

export function createMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      handleChunkLoadError(error, "mutation");
      if (mutation.meta?.skipErrorToast) return;
      const title = resolveMetaMessage(mutation.meta?.errorMessage);
      if (!title) return;
      addToast({
        title,
        description: resolveMetaMessage(mutation.meta?.errorDescription),
        severity: "danger",
        error,
      });
    },
  });
}

const mutationCache = createMutationCache();
const queryCache = createQueryCache();

const isClientError = (error: unknown): boolean => {
  const status = apiErrorStatus(error);
  return status !== undefined && `${status}`.startsWith("4");
};

// behavior flags (staleTime, refetchOnWindowFocus, ...) stay on library
// defaults in BOTH modes so refetch-driven bugs reproduce locally; only
// retry count/pacing differs, so a dead local backend fails fast
export function createQueryDefaults(
  mode: "development" | "production",
): DefaultOptions {
  const maxRetries = mode === "development" ? 2 : 3;

  return {
    queries: {
      retry: (failureCount, error) => {
        if (isClientError(error)) return false;
        return failureCount < maxRetries;
      },
      ...(mode === "development" && {
        retryDelay: (attemptIndex: number) =>
          Math.min(500 * 2 ** attemptIndex, 2000),
      }),
    },
  };
}

export const queryClient = new QueryClient({
  defaultOptions: createQueryDefaults(
    process.env.NODE_ENV === "development" ? "development" : "production",
  ),
  queryCache,
  mutationCache,
});

// persist conservatively (just the user: stable, safe to show stale on cold load);
// clear aggressively on 401 (see api-client.ts) so revoked sessions don't read back persisted data.
const PERSISTED_QUERY_IDS = new Set(["getCurrentUser"]);

const persister = createAsyncStoragePersister({
  storage: globalThis.localStorage,
  key: PERSISTED_USER_CACHE_KEY,
});

export const shouldDehydrateUserQuery: NonNullable<
  NonNullable<
    PersistQueryClientOptions["dehydrateOptions"]
  >["shouldDehydrateQuery"]
> = (query) => {
  if (query.state.status !== "success") return false;
  const head = query.queryKey[0];
  const id =
    head && typeof head === "object" && "_id" in head
      ? (head as { _id?: unknown })._id
      : null;
  return typeof id === "string" && PERSISTED_QUERY_IDS.has(id);
};

export const persistOptions: Omit<PersistQueryClientOptions, "queryClient"> = {
  persister,
  maxAge: 24 * 60 * 60 * 1000,
  buster: appVersion(),
  dehydrateOptions: {
    shouldDehydrateQuery: shouldDehydrateUserQuery,
  },
};
